import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './UI/button';
import { Card, CardDescription, CardHeader, CardTitle } from './UI/card';
import { Input } from './UI/input';
import { Label } from './UI/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './UI/dialog';
import { Coffee, Users, Calendar, DollarSign, Shield, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { login, register } from '../services/authService';
import { setUserData } from '../utils/authUtils';
import { useAuth } from '../contexts/AuthContext';

type UserType = 'seeker' | 'professional';

export default function LandingPageNew() {
  const navigate = useNavigate();
  // @ts-ignore - refreshAuthState exists in AuthContext but type definition is missing
  const { refreshAuthState } = useAuth();
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [showSignInDialog, setShowSignInDialog] = useState(false);
  const [registerType, setRegisterType] = useState<UserType>('seeker');
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [signInData, setSignInData] = useState({
    email: '',
    password: ''
  });

  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    // Normal user fields
    university: '',
    major: '',
    // Professional-specific fields
    company: '',
    position: '',
    linkedinUrl: '',
    // Common field
    headline: ''
  });

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare data for registration based on user type
      const registrationData = {
        ...registerData,
        userType: registerType,
        // Add default values for required fields based on user type
        ...(registerType === 'professional' ? {
          industry: '',
          yearsOfExperience: '0',
          expertise: [],
          bio: '',
          linkedinUrl: registerData.linkedinUrl,
          availabilityPerWeek: '0',
          meetingPreference: 'both'
        } : {
          university: '',
          major: '',
          graduationYear: '',
          interests: [],
          careerGoals: '',
          linkedinUrl: ''
        })
      };

      const response = await register(registrationData);

      if (response.success) {
        setShowRegisterDialog(false);
        setShowRegisterForm(false);

        // Store auth data
        if (response.data?.token && response.data?.user) {
          setUserData({ user: response.data.user, token: response.data.token });
          refreshAuthState();

          // Navigate to dashboard or profile setup based on user type
          navigate('/dashboard');
        }
      } else {
        toast.error(response.message || 'Registration failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.message || 'An error occurred during registration.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await login(signInData);

      if (response.success && response.data) {
        const { token, user } = response.data;

        if (!token || !user || !user._id) {
          toast.error('Login failed: Invalid server response.');
          return;
        }

        // Store auth data
        setUserData({ user, token });
        refreshAuthState();

        setShowSignInDialog(false);
        navigate('/dashboard');
      } else {
        toast.error(response.message || 'Login failed. Please check your credentials.');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'An error occurred during login.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openRegisterDialog = () => {
    setShowRegisterDialog(true);
    setShowRegisterForm(false);
  };

  const selectRegisterType = (type: UserType) => {
    setRegisterType(type);
    setShowRegisterForm(true);
  };

  const openSignInDialog = () => {
    setShowSignInDialog(true);
  };

  const switchToSignIn = () => {
    setShowRegisterDialog(false);
    setShowRegisterForm(false);
    setShowSignInDialog(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="border-b bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coffee className="h-8 w-8 text-blue-600" />
            <span className="text-2xl font-semibold">GrabACoffee</span>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={openSignInDialog}>
              Sign In
            </Button>
            <Button onClick={() => openRegisterDialog()}>
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-5xl font-bold mb-6 text-gray-900 dark:text-white">
          Connect Over Coffee,<br />Grow Your Network
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
          Book paid coffee chats with industry professionals. Share your expertise or learn from the best in your field.
        </p>
        <div className="flex gap-4 justify-center">
          <Button size="lg" onClick={() => openRegisterDialog()}>
            Find Professionals
          </Button>
          <Button size="lg" variant="outline" onClick={() => openRegisterDialog()}>
            Become a Professional
          </Button>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
          Why GrabACoffee?
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <Card>
            <CardHeader>
              <Users className="h-12 w-12 text-blue-600 mb-4" />
              <CardTitle>Expert Network</CardTitle>
              <CardDescription>
                Connect with verified professionals across industries
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <Calendar className="h-12 w-12 text-blue-600 mb-4" />
              <CardTitle>Easy Scheduling</CardTitle>
              <CardDescription>
                Book sessions that fit your schedule with instant confirmation
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <DollarSign className="h-12 w-12 text-blue-600 mb-4" />
              <CardTitle>Secure Payments</CardTitle>
              <CardDescription>
                Safe and secure payment processing for all transactions
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <Clock className="h-12 w-12 text-blue-600 mb-4" />
              <CardTitle>Flexible Sessions</CardTitle>
              <CardDescription>
                Choose from 30 or 60-minute sessions based on your needs
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <Shield className="h-12 w-12 text-blue-600 mb-4" />
              <CardTitle>Verified Profiles</CardTitle>
              <CardDescription>
                All professionals are verified to ensure quality connections
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <Coffee className="h-12 w-12 text-blue-600 mb-4" />
              <CardTitle>Virtual or In-Person</CardTitle>
              <CardDescription>
                Meet over video call or at your favorite coffee shop
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 py-16 bg-blue-50 dark:bg-gray-800 -mx-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
            How It Works
          </h2>
          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold">
                1
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Create Your Profile</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Sign up to book sessions or as a professional to offer your expertise
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Browse & Book</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Search for professionals by industry, expertise, or availability and book a session
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold">
                3
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Connect & Learn</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Meet for your scheduled coffee chat and build meaningful professional connections
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white dark:bg-gray-900 mt-16">
        <div className="container mx-auto px-4 py-8 text-center text-gray-600 dark:text-gray-400">
          <p>© 2025 GrabACoffee. All rights reserved.</p>
        </div>
      </footer>

      {/* Registration Dialog */}
      <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
        <DialogContent>
          {!showRegisterForm ? (
            <>
              <DialogHeader>
                <DialogTitle>Create Your Account</DialogTitle>
                <DialogDescription>
                  Choose how you want to join GrabACoffee
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Button
                  onClick={() => selectRegisterType('seeker')}
                  className="w-full h-auto py-4 flex flex-col items-start gap-1"
                  variant="outline"
                >
                  <span className="font-semibold">Register</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Join to book sessions with professionals
                  </span>
                </Button>
                <Button
                  onClick={() => selectRegisterType('professional')}
                  className="w-full h-auto py-4 flex flex-col items-start gap-1"
                  variant="outline"
                >
                  <span className="font-semibold">Register as Professional</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Share your expertise and earn by hosting sessions
                  </span>
                </Button>
              </div>
              <div className="text-center pt-4 border-t dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={switchToSignIn}
                    className="text-blue-600 hover:underline"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  {registerType === 'professional' ? 'Register as Professional' : 'Create Account'}
                </DialogTitle>
                <DialogDescription>
                  Fill in your details to get started
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      required
                      value={registerData.firstName}
                      onChange={(e) => setRegisterData({...registerData, firstName: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      required
                      value={registerData.lastName}
                      onChange={(e) => setRegisterData({...registerData, lastName: e.target.value})}
                    />
                  </div>
                </div>
                {registerType === 'professional' ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="company">Company</Label>
                        <Input
                          id="company"
                          placeholder="Google"
                          required
                          value={registerData.company}
                          onChange={(e) => setRegisterData({...registerData, company: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="position">Position</Label>
                        <Input
                          id="position"
                          placeholder="Senior Software Engineer"
                          required
                          value={registerData.position}
                          onChange={(e) => setRegisterData({...registerData, position: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
                      <Input
                        id="linkedinUrl"
                        placeholder="https://www.linkedin.com/in/yourprofile"
                        required
                        value={registerData.linkedinUrl}
                        onChange={(e) => setRegisterData({...registerData, linkedinUrl: e.target.value})}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="university">University / Organization</Label>
                      <Input
                        id="university"
                        placeholder="Stanford University"
                        required
                        value={registerData.university}
                        onChange={(e) => setRegisterData({...registerData, university: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="major">Major / Role</Label>
                      <Input
                        id="major"
                        placeholder="Computer Science"
                        required
                        value={registerData.major}
                        onChange={(e) => setRegisterData({...registerData, major: e.target.value})}
                      />
                    </div>
                  </>
                )}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    required
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={registerData.password}
                    onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Min 8 characters with uppercase, lowercase, number and special character
                  </p>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    required
                    value={registerData.confirmPassword}
                    onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowRegisterForm(false)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button type="submit" className="flex-1" disabled={isSubmitting}>
                    {isSubmitting ? 'Creating...' : 'Create Account'}
                  </Button>
                </div>
                <div className="text-center pt-2 border-t dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={switchToSignIn}
                      className="text-blue-600 hover:underline"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Sign In Dialog */}
      <Dialog open={showSignInDialog} onOpenChange={setShowSignInDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Sign In
            </DialogTitle>
            <DialogDescription>
              Enter your credentials to access your account
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSignIn} className="space-y-4">
            <div>
              <Label htmlFor="signin-email">Email</Label>
              <Input
                id="signin-email"
                type="email"
                placeholder="john@example.com"
                required
                value={signInData.email}
                onChange={(e) => setSignInData({...signInData, email: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="signin-password">Password</Label>
              <Input
                id="signin-password"
                type="password"
                required
                value={signInData.password}
                onChange={(e) => setSignInData({...signInData, password: e.target.value})}
              />
            </div>
            <div className="text-right">
              <button
                type="button"
                onClick={() => {
                  setShowSignInDialog(false);
                  navigate('/forgot-password');
                }}
                className="text-sm text-blue-600 hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
